import {
  type PlayerItemResponse,
  type PlayerResponse,
  rdbV2LatestList,
  type RdbV2LatestListParams,
} from '@on3/api';
import type {
  ErrorData,
  ICommitsIndexProps,
} from '@on3/ui-lib/api/schema/custom-contracts';
import { Toggle } from '@on3/ui-lib/components/Toggle/Toggle';
import { externalApi, useToggle } from '@on3/ui-lib/index';
import { useSite } from '@on3/ui-lib/src/contexts/SiteProvider';
import { slugify } from '@on3/ui-lib/utils/slugify';
import { type AxiosError } from 'axios';
import { InterestsNav } from 'components/College/Interests/Nav';
import { IndustryComparisonListAlt } from 'components/Db/Rankings/IndustryComparison/IndustryComparisonListAlt';
import { AdSidebarLayout } from 'components/Layouts/AdSidebarLayout';
import { useRouter } from 'next/router';
import { useCallback, useMemo, useState } from 'react';

import { InterestLegend } from '../General/InterestLegend';
import { InterestListFilters } from '../General/InterestListFilters';
import { TeamRanking } from '../TeamRank/TeamRanking';
import styles from './CommitList.module.scss';
import { CommitListItem } from './CommitListItem';
import { CommitColumnHeaders } from './Header';

export enum StatusType {
  Enrolled = 'Enrolled',
  Signed = 'Signed',
  Committed = 'Committed',
  Transferred = 'Transferred',
  None = 'None',
}

export const StatusTypeValue = {
  [StatusType.Enrolled]: 1,
  [StatusType.Signed]: 2,
  [StatusType.Committed]: 3,
  [StatusType.Transferred]: 4,
  [StatusType.None]: 5,
};

export const getStatusType = (value?: string | null): StatusType => {
  return (value as StatusType) ?? StatusType.None;
};

export type sortListType = 'commits' | 'transfers' | 'decommits';

export const useSortList = (
  list?: PlayerItemResponse[] | null,
  listType?: sortListType,
) =>
  useCallback(
    (sortBy: keyof PlayerResponse | string, ascending: boolean) => {
      if (!list?.length) {
        return list;
      }

      const isName = sortBy === 'lastName';
      const isHeight = sortBy === 'height';
      const isWeight = sortBy === 'weight';
      const isStatus = sortBy === 'status';
      const isPosition = sortBy === 'position';
      const isRating = sortBy === 'rating';
      const isNilValue = sortBy === 'nilValue';
      const isVerified = sortBy === 'athleteVerified';
      const isStatusDate = sortBy === 'date';
      const sortedList = [...list]?.sort((a, b) => {
        let setA = null;
        let setB = null;

        if (isName || isVerified || isWeight || isHeight) {
          setA = String(a.player?.[sortBy]);
          setB = String(b.player?.[sortBy]);
        }

        if (isPosition) {
          setA = a?.player?.position?.name;
          setB = b?.player?.position?.name;
        }

        if (isRating) {
          setA =
            listType === 'transfers'
              ? a.transferRating?.rating
              : a.rating?.consensusRating;
          setB =
            listType === 'transfers'
              ? b.transferRating?.rating
              : b.rating?.consensusRating;
        }

        if (isNilValue) {
          setA = a?.nilValue?.totalValue || 0;
          setB = b?.nilValue?.totalValue || 0;
        }

        if (isStatus) {
          const statusA = a.status?.transferredAsset
            ? 'Transferred'
            : a.status?.type;
          const statusB = b.status?.transferredAsset
            ? 'Transferred'
            : b.status?.type;

          const statusTypeA = getStatusType(statusA);
          const statusTypeB = getStatusType(statusB);

          const statusValueA = StatusTypeValue[statusTypeA];
          const statusValueB = StatusTypeValue[statusTypeB];

          setA = statusValueA;
          setB = statusValueB;
        }

        if (isStatusDate) {
          setA = a?.status?.date;
          setB = b?.status?.date;
        }

        //  puts nulls at the end of list
        if (!setA && setA !== 0) {
          return 1;
        }

        if (!setB && setB !== 0) {
          return -1;
        }

        if (setA < setB) {
          return ascending ? 1 : -1;
        }

        if (setA > setB) {
          return ascending ? -1 : 1;
        }

        return 0;
      });

      return sortedList;
    },
    [list, listType],
  );

export const CommitList = ({
  playerList,
  teams,
  years,
  teamRank,
  title,
  toggled = false,
}: ICommitsIndexProps) => {
  const router = useRouter();
  const { sport, year, page, status: queryStatus } = router.query;
  const defaultPage = parseInt(`${page}`, 10);
  const { rankingSports } = useSite();

  const pagination = playerList?.pagination;
  const isTeam = router.query.college;
  const isIndustryComparison = router.asPath.includes(
    'industry-comparison-commits',
  );
  const isLatestCommits = router.asPath.includes('latest-commits');
  const isIndustrySport =
    rankingSports?.find((sportData) => slugify(sportData.name) === sport)
      ?.isIndustryRankable || false;
  const showToggle = !isLatestCommits && isIndustrySport;
  const [list, setList] = useState(
    queryStatus
      ? playerList?.list?.filter(
          (item) =>
            item.status?.type?.toLowerCase() === `${queryStatus}` &&
            !item.status.transferredAsset,
        )
      : playerList?.list,
  );
  const [pageNumber, setPageNumber] = useState(defaultPage || 1);
  const [disable, setDisable] = useState(false);

  const [selected, setSelected] = useState('rating');
  const [ascending, setAscending] = useState(true);
  const [toggle, setToggle] = useToggle(toggled);

  const sortList = useSortList(
    queryStatus
      ? playerList?.list?.filter(
          (item) =>
            item.status?.type?.toLowerCase() === `${queryStatus}` &&
            !item.status.transferredAsset,
        )
      : playerList?.list,
  );
  const handleToggle = useCallback(() => {
    if (isTeam) {
      const newPath = isIndustryComparison
        ? '/college/[college]/[sport]/[year]/commits'
        : '/college/[college]/[sport]/[year]/industry-comparison-commits';

      router.push({
        pathname: newPath,
        query: { ...router.query },
      });
    }

    setToggle((prevState: boolean) => !prevState);
  }, [isIndustryComparison, isTeam, router, setToggle]);

  const statusCounts = useMemo(() => {
    return list?.reduce(
      (acc, item) => {
        if (item.status?.transferEntered) {
          acc.transfers += 1;
        } else {
          acc.commits += 1;
        }

        return acc;
      },
      { commits: 0, transfers: 0 },
    );
  }, [list]);
  const showLoadMoreButton =
    !!pagination?.count &&
    pagination?.count > 50 &&
    (pagination?.pageCount ?? 0) > pageNumber &&
    list &&
    list?.length < pagination?.count;

  const handleClick: React.MouseEventHandler = useCallback(async () => {
    setDisable(true);
    const newPageNumber = pageNumber + 1;
    const sportKey = rankingSports?.find(
      (sp) => sp?.name && slugify(sp?.name) === sport,
    )?.key;

    const params: RdbV2LatestListParams = {
      sport: sportKey,
      year: Number(year) || undefined,
      page: newPageNumber,
    };

    try {
      const response = await rdbV2LatestList(externalApi, params);

      const currentPage = response.pagination?.currentPage ?? 0;

      if (currentPage) {
        router.replace(
          {
            query: { ...router.query, page: currentPage },
          },
          undefined,
          { shallow: true },
        );
        list && setList([...list, ...(response?.list || [])]);

        setPageNumber(currentPage);
      }
    } catch (e: unknown) {
      const { response, message }: AxiosError<ErrorData> =
        e as AxiosError<ErrorData>;

      console.error(
        'Latest Commits Error: ',
        response?.data?.message || message,
      );
    } finally {
      setDisable(false);
    }
  }, [list, pageNumber, rankingSports, router, sport, year]);

  const handleChange: React.MouseEventHandler<HTMLButtonElement> = useCallback(
    (e) => {
      const { dataset } = e.currentTarget;
      const { sorttype = '', flip = 'false' } = dataset;
      //  flips order of the list
      const isFlipped = flip === 'true';
      let orderBy = ascending;

      if (sorttype !== selected) {
        setSelected(sorttype);
        orderBy = isFlipped;
        setAscending(isFlipped);
      } else {
        orderBy = !ascending;
        setAscending((prev) => !prev);
      }

      const sortedList = sortList(sorttype, orderBy);

      sortedList && setList(sortedList);
    },
    [ascending, selected, sortList],
  );

  return (
    <AdSidebarLayout>
      <section className={styles.block}>
        <header className={styles.header}>
          <h1>{title}</h1>

          <div className={styles.filterWrapper}>
            <InterestListFilters teams={teams} years={years} />
            {showToggle && (
              <Toggle
                checked={toggle}
                id="industry-comparison-toggle"
                label="Industry Comparison"
                onChange={handleToggle}
              />
            )}
          </div>
        </header>
        {(!isLatestCommits || teamRank) && (
          <div className={styles.container}>
            {!isLatestCommits && <InterestsNav />}

            {teamRank && (
              <TeamRanking statusCounts={statusCounts} teamRank={teamRank} />
            )}
          </div>
        )}

        {toggle ? (
          <IndustryComparisonListAlt playerList={list} />
        ) : (
          <div className={styles.listWrapper}>
            {list?.length ? (
              <>
                <CommitColumnHeaders
                  ascending={ascending}
                  noSorting={isLatestCommits}
                  onChange={handleChange}
                  selected={selected}
                />
                <ul className={styles.playerList}>
                  {list.map((item, index) => (
                    <li
                      key={`${item.psoKey}-${index}-${selected}-${ascending}`}
                    >
                      <CommitListItem data={item} />
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <p className={styles.noListText}>
                There are no players for this selection.
              </p>
            )}
          </div>
        )}
        {showLoadMoreButton && isLatestCommits && (
          <button
            className={styles.btnLoadMore}
            disabled={disable}
            onClick={handleClick}
          >
            Load More
          </button>
        )}
        {!!list?.length && <InterestLegend className={styles.legend} />}
      </section>
    </AdSidebarLayout>
  );
};
